import { useEffect, useState, useCallback } from "react";
import {
  Alert,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import "../../AppPage.css";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../store/auth/hooks";
import { ContentTitleBar } from "../../../components/ContentTitleBar";
import useWebSocket from "react-use-websocket";
import { WEBSOCKET_URL } from "../../../store/config";
import { loadClip } from "../utils/dataLoader";
import { checkStatusWhitelist } from "../../../store/navigationUtil";
import { clipStatus } from "../utils/clipStatus";
import {
  BECOMING_SINGLE_CHANNEL,
  ASI_FILTER_RUNNING,
  NLMS_FILTER_RUNNING,
  NORMALIZING_FILTER,
  FILTERING,
  TRIMMING,
  EXTRACTING,
  SYNC_FILTER_RUNNING,
} from "../../../store/statuses";

export default function FilterRunningPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { clipId, caseId } = useParams();
  const [clip, setClip] = useState(null);
  const { accessCode } = useAuth();
  const [totalFiles, setTotalFiles] = useState(0);
  const [completedFiles, setCompletedFiles] = useState(0);
  const [websocketRegistered, setWebsocketRegistered] = useState(false);
  const [filterType, setFilterType] = useState("FILTERING");

  const percentComplete =
    totalFiles > 0 ? (completedFiles / totalFiles) * 100.0 : 0;

  const load = useCallback(async () => {
    const loadedClip = await loadClip(caseId, clipId);
    if (!loadedClip) {
      navigate(`/app/cases/${caseId}/clips`);
    }
    if (
      !checkStatusWhitelist(loadedClip, [
        FILTERING,
        EXTRACTING,
        TRIMMING,
        NORMALIZING_FILTER,
        SYNC_FILTER_RUNNING,
        ASI_FILTER_RUNNING,
        NLMS_FILTER_RUNNING,
        BECOMING_SINGLE_CHANNEL,
      ])
    ) {
      navigate(`/app/cases/${caseId}/clips/${clipId}`);
    }
    setClip(loadedClip);
    if (loadedClip.status === NORMALIZING_FILTER) {
      setFilterType("NORMALIZING");
    } else {
      setFilterType(clipStatus[loadedClip.status].label.toUpperCase());
    }
  }, [caseId, clipId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Setup a websocket
   */
  const { sendMessage } = useWebSocket(WEBSOCKET_URL, {
    share: true,
    onOpen: () => {
      console.log("WebSocket connection established.", WEBSOCKET_URL);
    },
    onMessage: (event) => {
      if (event.data && event.data) {
        const data = JSON.parse(event.data);
        console.log("[WEBSOCKET]", data);
        if (
          data.command === "CLIP_STATUS" &&
          data.data.clipId === Number(clipId) &&
          data.data.clipStatus !== "FILTERING" &&
          data.data.clipStatus !== "EXTRACTING" &&
          data.data.clipStatus !== "TRIMMING"
        ) {
          console.log(
            "[FilterRunninPage][WebSocket] New Status",
            data.data.clipStatus,
          );
          navigate(`/app/cases/${caseId}/clips/${clipId}`);
        } else if (
          (data.command === "FILTER_STATUS_MESSAGE" ||
            data.command === "NORMALIZATION_MESSAGE") &&
          data.data.clipId === Number(clipId)
        ) {
          //Set State for total and completed files
          console.log(
            "[FilterRunningPage][WebSocket] PERCENT COMPLETE",
            data.data,
          );
          setTotalFiles(data.data.totalFiles);
          setCompletedFiles(data.data.completedFiles);
        }
      }
    },
  });

  useEffect(() => {
    if (!websocketRegistered) {
      setWebsocketRegistered(true);
      const payload = {
        command: "REGISTER",
        authorization: accessCode,
      };
      sendMessage(JSON.stringify(payload));
    }
  }, [accessCode, websocketRegistered, sendMessage]);

  return (
    <Stack component="main" height={1}>
      <Stack flex={1} gap={2} p={2}>
        <Paper elevation={4} sx={{ p: 2 }}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid
              size={{ xs: 12, sm: 4, md: 3 }}
              display="flex"
              flexDirection="column"
            >
              <Typography variant="caption">STATUS</Typography>
              <Chip
                sx={{
                  alignSelf: "start",
                  backgroundColor: "#00b976",
                  color: theme.palette.common.white,
                }}
                label={filterType}
              />
            </Grid>
            <Grid
              size={{ xs: 12, sm: 4, md: 3 }}
              display="flex"
              flexDirection="column"
            >
              <Typography variant="caption">PROCESSED AUDIO FILES</Typography>
              <Typography variant="h5">{totalFiles}</Typography>
            </Grid>
            <Grid
              size={{ xs: 12, sm: 4, md: 3 }}
              display="flex"
              flexDirection="column"
            >
              <Typography variant="caption">PROGRESS</Typography>
              <Typography variant="h5">{percentComplete.toFixed()}%</Typography>
            </Grid>
          </Grid>
        </Paper>
        <Alert
          severity="info"
          variant="filled"
          sx={{ bgcolor: theme.palette.primary.dark, boxShadow: 4 }}
        >
          The system is now {filterType.toLowerCase()} the audio files. This may
          take a few minutes.
        </Alert>
      </Stack>
      <Paper
        elevation={4}
        sx={{
          p: 1,
          mx: 1,
          mb: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={async () => navigate(`/app/cases/${caseId}/clips`)}
        >
          RETURN TO CLIPS
        </Button>
      </Paper>
      <ContentTitleBar name={clip?.description} label="Clip Source Media" />
    </Stack>
  );
}
